import React, { useEffect, useMemo, useState } from 'react';
import { setField, addKeyword, removeKeyword, setVideoLevel } from '@/redux/formData';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DatePicker from 'react-datepicker';
import Form from 'react-bootstrap/Form';
import 'react-quill/dist/quill.snow.css';
import { FormControl, InputLabel, Checkbox, ListItemText, Card, CardContent, TextField } from '@mui/material';
import '@/Adminassets/TagInput.css';
import '@/Adminassets/Style.css';
import { useFetchInstructors } from '@/data/swrs/instructor';
import { useAtomValue } from 'jotai';
import ReactQuill from 'react-quill';
import dayjs from 'dayjs';
import { ProductsDropdown } from '@/AdminComponent/ProductsDropdown';
import { addVideoConstants } from '@/constants/addVideo';
import { TagsDropdown } from '@/AdminComponent/TagsDropdown';
import { InstructorDropdown } from '@/AdminComponent/InstructorDropdown';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { useFetchCategories } from '@/data/swrs/videos';
import Toast from '@/utils/toastManager';
import { newYorkMidnight } from '@/utils/helper';
import VideoUploadSelector from '@/AdminComponent/AddVideoForm/components/VideoUploadSelector';
import ReleaseSchedule from '@/AdminComponent/components/ReleaseSchedule';

const AddVideoDetails = ({ formData, dispatch }) => {
	const userData = useAtomValue(userInfoAtom);
	const token = useMemo(() => userData?.token, [userData]);
	const { isError } = useFetchInstructors(token);
	const { videoTypes, isError: isCategoryError } = useFetchCategories(token);
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [keywordQuery, setKeywordQuery] = useState('');
	const [releaseDate, setReleaseDate] = useState(formData.dateOnly ? new Date(formData.dateOnly) : null);

	useEffect(() => {
		if (isError) {
			Toast.error('Something went wrong while fetching instructors');
			console.error('Error fetching instructors:', isError);
		}
	}, [isError]);

	useEffect(() => {
		if (isCategoryError) {
			Toast.error('Something went wrong');
			console.error('Error fetching categories:', isCategoryError);
		}
	}, [isCategoryError]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setSuccessMessage('');
			setErrorMessage('');
		}, 20000);

		return () => clearTimeout(timer);
	}, [successMessage, errorMessage]);

	const handleChange = e => {
		dispatch(setField(e.target.name, e.target.value));
	};

	const handleKeyWordAdd = newKeyword => {
		if (newKeyword?.trim().length > 0 && !formData?.keywords.some(k => k.name === newKeyword)) {
			dispatch(addKeyword({ name: newKeyword }));
		}
	};

	const handleKeywordRemove = keyword => {
		dispatch(removeKeyword(keyword));
	};
	const handleVideoLevelChange = event => {
		const { value } = event.target;
		dispatch(setVideoLevel(value));
	};
	function formatDuration(seconds) {
		const totalSeconds = Math.floor(seconds);
		const minutes = Math.floor(totalSeconds / 60);
		const remainingSeconds = totalSeconds % 60;
		const paddedMinutes = String(minutes).padStart(2, '0');
		const paddedSeconds = String(remainingSeconds).padStart(2, '0');

		return `${paddedMinutes}:${paddedSeconds}`;
	}

	const onReleaseDateChange = date => {
		setReleaseDate(date);
		const dateOnly = date ? dayjs(date).format('YYYY-MM-DD') : '';
		// Store only a calendar date; backend will convert to > 00:00 America/New_York > UTC
		dispatch(setField('dateOnly', dateOnly));
	};
	return (
		<div>
			{successMessage && <div className="alert-success">{successMessage}</div>}
			{errorMessage && <div className="alert-danger">{errorMessage}</div>}
			<VideoUploadSelector formData={formData} dispatch={dispatch} />
			<Card className="p-3">
				<CardContent>
					<Form>
						<Form.Group controlId="muxVideoLink">
							<Form.Label>Mux Video Link</Form.Label>
							<Form.Control
								type="text"
								name="muxVideoLink"
								placeholder="Video Link"
								value={formData.muxVideoLink}
								disabled
							/>
						</Form.Group>

						<Form.Group controlId="title">
							<Form.Label>Title</Form.Label>
							<Form.Control
								type="text"
								name="title"
								placeholder="Enter title"
								value={formData.title}
								onChange={handleChange}
								disabled
							/>
						</Form.Group>
						<div className="mt-2">
							<InstructorDropdown
								value={formData[addVideoConstants.ControlIds.INSTRUCTOR] || ''}
								onChange={val => dispatch(setField(addVideoConstants.ControlIds.INSTRUCTOR, val))}
							/>
						</div>
						<Form.Group controlId="description">
							<Form.Label>Description</Form.Label>
							<ReactQuill value={formData.description} onChange={value => dispatch(setField('description', value))} />
						</Form.Group>
						{formData.isClass && (
							<>
								<Form.Group controlId="muxTrailerLink">
									<Form.Label>Mux Trailer Link</Form.Label>
									<Form.Control
										type="text"
										name="muxTrailerLink"
										placeholder="Trailer Link"
										value={formData.muxTrailerLink}
										disabled
									/>
								</Form.Group>
								<Form.Group controlId="shortDescription">
									<Form.Label>Short Description</Form.Label>
									<Form.Control
										type="text"
										placeholder="Enter a short description"
										value={formData.shortDescription}
										name="shortDescription"
										onChange={handleChange}
									/>
								</Form.Group>
								<Form.Group controlId="duration">
									<Form.Label>Trailer Duration</Form.Label>
									<Form.Control
										type="text"
										name="trailerDuration"
										placeholder="Enter Duration"
										value={formatDuration(formData.trailerDuration)}
										onChange={handleChange}
										disabled
									/>
								</Form.Group>
							</>
						)}
						{!formData.isClass && (
							<FormControl className="mt-1" fullWidth>
								<InputLabel id="category-label">Video Category</InputLabel>
								<Select
									labelId="category-label"
									id="CategoryId"
									name="CategoryId"
									value={formData.CategoryId}
									label="Video Category"
									onChange={e => handleChange(e)}
								>
									<MenuItem value="">
										<em>Select a category</em>
									</MenuItem>
									{videoTypes?.map((type, index) => (
										<MenuItem key={index} value={type._id}>
											{type.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						<FormControl className="mt-1" fullWidth>
							<InputLabel id="video-level">Choose the Level</InputLabel>
							<Select
								labelId="video-level"
								id="videoLevel"
								name="videoLevel"
								multiple
								value={formData.videoLevel}
								onChange={handleVideoLevelChange}
								label="Choose the Level"
								renderValue={selected => selected.join(', ')}
							>
								<MenuItem value="" disabled>
									<em>Select The Level of the Video</em>
								</MenuItem>
								<MenuItem value="Beginner">
									<Checkbox checked={formData.videoLevel.includes('Beginner')} />
									<ListItemText primary="Beginner" />
								</MenuItem>
								<MenuItem value="Intermediate">
									<Checkbox checked={formData.videoLevel.includes('Intermediate')} />
									<ListItemText primary="Intermediate" />
								</MenuItem>
								<MenuItem value="Advanced">
									<Checkbox checked={formData.videoLevel.includes('Advanced')} />
									<ListItemText primary="Advanced" />
								</MenuItem>
							</Select>
						</FormControl>

						<TagsDropdown
							value={formData[addVideoConstants.ControlIds.TAGS]}
							onChange={tags => dispatch(setField(addVideoConstants.ControlIds.TAGS, tags))}
						/>
						<Form.Label>Keywords</Form.Label>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<TextField
								label="Add Keyword"
								value={keywordQuery}
								onChange={e => setKeywordQuery(e.target.value)}
								onKeyDown={e => {
									if (e.key === 'Enter' && keywordQuery.trim()) {
										handleKeyWordAdd(keywordQuery);
										setKeywordQuery('');
										e.preventDefault();
									}
								}}
								fullWidth
							/>
							<div
								style={{
									display: 'flex',
									flexWrap: 'wrap',
									marginRight: '0.32rem'
								}}
							>
								{formData?.keywords.map((keyword, index) => (
									<div key={index} className="tag">
										{keyword.name} <span onClick={() => handleKeywordRemove(keyword)}>x</span>
									</div>
								))}
							</div>
						</div>

						<div className="mt-2">
							<ProductsDropdown
								value={formData[addVideoConstants.ControlIds.PRODUCTS]}
								onChange={products => dispatch(setField(addVideoConstants.ControlIds.PRODUCTS, products))}
							/>
						</div>
						<ReleaseSchedule
						releaseDate={releaseDate}
						onReleaseDateChange={onReleaseDateChange}
						newYorkMidnight={newYorkMidnight}
						/>
						
					</Form>
				</CardContent>
			</Card>
		</div>
	);
};

export default AddVideoDetails;
